/**
 * Front Desk / Kiosk view state (DNKADM-0031).
 *
 * A single boolean — is the console in Front Desk view? — persisted in
 * **`sessionStorage`** so a reload stays in Front Desk view. This is a NON-SECRET
 * UI flag, NOT a token: it does NOT violate the Option-A no-token-persistence
 * invariant (the access token stays in-memory only; see `authStore`). It is read
 * once on boot so a reload restores the mode, and defaults OFF.
 *
 * Front Desk view restricts the UI to Operations (hiding Configuration) and gates
 * exiting back to the full view behind the admin's own password — see
 * `RequireAdmin` (route gate), `AppLayout` (menu + indicator + enter/exit), and
 * `verifyPassword` (the exit re-auth check). It is a UI gate on the admin's
 * existing session, NOT a server-enforced scope change.
 *
 * Unavailable to a platform-support session (DNKADM-0356). Exiting the mode
 * re-authenticates with the admin's own password via `POST /me/verify-password`,
 * which resolves the user through the TENANT'S MEMBERSHIP ROW — and a
 * `PLATFORM_SUPPORT` principal has none, by construction. The correct password
 * would come back `valid:false`, so the dialog would say "incorrect password" and
 * lock the UI after five tries, trapping the support user until they log out. The
 * fix is to never enter: for that principal `frontDeskView` is forced off,
 * `enterFrontDeskView` is a no-op, and the persisted flag is cleared so a stale
 * one from an earlier ordinary session on the same browser cannot re-enter it.
 * The exit dialog itself is untouched — with this it is simply unreachable.
 *
 * Unavailable to a STAFF session for the same mechanism and one more reason
 * (DNKADM-0359): STAFF is ALREADY the restricted view — no money, no settings,
 * no team — so the mode has nothing left to hide, and its exit re-auth resolves
 * through the same membership row the customer/staff split moves out from under
 * staff. Both principals take the identical path below (`frontDeskUnavailable`).
 *
 * Unavailable, TEMPORARILY, to every `TENANT_STAFF` session — OWNER and ADMIN
 * included (DNKADM-0368, on dink-api's verified REPLY §c). After the split's
 * cutover an admin whose only membership row moved to `tenant_staff` gets
 * `valid:false` for their correct password from the very same lookup, so the
 * trap above becomes theirs too. saas-auth has specced the fix (SASATH-0321);
 * when the Operator announces it on their staging, one line lifts this for all
 * staff tiers at once — there is deliberately no flag or date switch to lift.
 *
 * All three now share ONE definition: `operator.frontDeskUnavailable`, derived
 * in `authContextValue`, so this provider and `AppLayout`'s entry control cannot
 * disagree about who has the mode.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { AuthContext } from './authContextValue';
import {
  FrontDeskContext,
  readFrontDeskView,
  writeFrontDeskView,
  type FrontDeskContextValue,
} from './frontDeskContextValue';

export interface FrontDeskProviderProps {
  children: ReactNode;
  /** Test seam — override the initial value (defaults to the persisted flag). */
  initialValue?: boolean | undefined;
}

export function FrontDeskProvider({ children, initialValue }: FrontDeskProviderProps) {
  // Read the operator TOLERANTLY — `useContext` directly rather than
  // `useCurrentOperator`, which throws without an AuthProvider above it. This
  // provider is mounted standalone in a few trees, and "no auth context" simply
  // means "an ordinary session".
  const operator = useContext(AuthContext)?.operator ?? null;
  // Platform support (DNKADM-0356), the STAFF floor (DNKADM-0359) and — until
  // saas-auth's SASATH-0321 lands — every TENANT_STAFF session (DNKADM-0368).
  // One SHARED definition, derived in `authContextValue`, so this provider and
  // `AppLayout`'s entry control can never drift apart. No auth context above
  // (the provider is mounted standalone in a few trees) means an ordinary
  // session, hence the `=== true`.
  const frontDeskUnavailable = operator?.frontDeskUnavailable === true;

  const [enteredFrontDeskView, setEnteredFrontDeskView] = useState<boolean>(
    () => initialValue ?? readFrontDeskView(),
  );

  // Forced off for those sessions (see the doc comment) — derived, so it holds
  // however the flag was seeded: persisted, the `initialValue` test seam, or an
  // enter that happened before the token resolved.
  const frontDeskView = frontDeskUnavailable ? false : enteredFrontDeskView;

  // Clear the PERSISTED flag for such a session: an ordinary session on this
  // browser may have left `sessionStorage` set, and that must not survive into a
  // support or STAFF session on the next sign-in. Syncing an external store is
  // what an effect is for; the render above already ignores the flag either way.
  useEffect(() => {
    if (!frontDeskUnavailable) return;
    writeFrontDeskView(false);
    setEnteredFrontDeskView(false);
  }, [frontDeskUnavailable]);

  const enterFrontDeskView = useCallback(() => {
    // No-op for those sessions — the mode's exit can never succeed for a
    // principal the password check cannot resolve to a tenant membership row.
    if (frontDeskUnavailable) return;
    writeFrontDeskView(true);
    setEnteredFrontDeskView(true);
  }, [frontDeskUnavailable]);

  const exitFrontDeskView = useCallback(() => {
    writeFrontDeskView(false);
    setEnteredFrontDeskView(false);
  }, []);

  const value = useMemo<FrontDeskContextValue>(
    () => ({ frontDeskView, enterFrontDeskView, exitFrontDeskView }),
    [frontDeskView, enterFrontDeskView, exitFrontDeskView],
  );

  return <FrontDeskContext.Provider value={value}>{children}</FrontDeskContext.Provider>;
}
